import type { FC } from "react";
import { Navigate } from "react-router-dom";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { StaffingViewLoading } from "../../StaffingViewLoading";
import { MonthlyJobView } from "./MonthlyJobView";
import { useMonthlyViewAvailable } from "./useMonthlyViewAvailable";

/**
 * Route guard for the Monthly view: it renders only where `useMonthlyViewAvailable` resolves to
 * `"available"` (flag on, non-medical department, enabled jobs present) — the exact same predicate
 * `StaffingDateTypeSelector` gates the Month option on, so opening the URL directly can't bypass the
 * rollout and the two can never disagree (no offer-then-redirect bounce).
 *
 * The third state is what makes that hold: while the predicate is still `"loading"` this waits instead
 * of deciding. Treating "department not resolved yet" as "unavailable" redirected out of a department
 * that does offer the view, and built the target from an empty `department_id` (`//staffing`). Because
 * `"unavailable"` can only resolve once the department has, the redirect below always names a real one.
 */
export const MonthlyViewRoute: FC = () => {
	const availability = useMonthlyViewAvailable();
	const { chosenDepartment } = useHospitalData();

	if (availability === "loading") return <StaffingViewLoading dataTestId="monthly-job-view-loading" />;
	if (availability === "unavailable") return <Navigate to={`/${chosenDepartment.department_id}/staffing`} replace />;
	return <MonthlyJobView />;
};
